import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * @typedef {Object} RecommendationCardProps
 * @property {React.ComponentType<{className?: string}>} icon
 * @property {string} title
 * @property {string} description
 * @property {string} actionLabel
 * @property {string} [actionHref] - if set, renders the CTA as a Link (via Button asChild)
 * @property {() => void} [onAction] - if set (and actionHref isn't), renders the CTA as a button click handler
 * @property {React.ComponentType<{className?: string}>} [actionIcon]
 * @property {'primary'|'info'} [tone]
 */

/**
 * Extracted from Dashboard.jsx's "Talk to Ritika" CTA card. Note: this is
 * intentionally scoped to that one pattern (icon + heading + description +
 * button, on a solid brand-color card) — AISIngestion.jsx's small AI
 * "Recommendation" note is a structurally different, simpler pattern (a
 * labeled inline note, no icon/button) and was left as-is rather than
 * forced into this component. See Batch 2.1 completion report.
 * @param {RecommendationCardProps} props
 */
export default function RecommendationCard({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  actionIcon: ActionIcon,
  tone = "primary",
}) {
  const toneCls = tone === "primary" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground";
  const bodyCls = tone === "primary" ? "text-primary-foreground/80" : "text-muted-foreground";
  const buttonCls = tone === "primary" ? "bg-accent hover:bg-accent/90 text-accent-foreground" : "";

  const cta = actionHref ? (
    <Button asChild className={`mt-5 ${buttonCls}`}>
      <Link to={actionHref}>
        {actionLabel}
        {ActionIcon && <ActionIcon className="ml-2 h-4 w-4" aria-hidden="true" />}
      </Link>
    </Button>
  ) : (
    <Button onClick={onAction} className={`mt-5 ${buttonCls}`}>
      {actionLabel}
      {ActionIcon && <ActionIcon className="ml-2 h-4 w-4" aria-hidden="true" />}
    </Button>
  );

  return (
    <Card className={`p-6 border-border ${toneCls}`}>
      {Icon && <Icon className="h-8 w-8" aria-hidden="true" />}
      <div className="font-display text-lg font-semibold mt-4">{title}</div>
      <p className={`mt-2 text-sm leading-relaxed ${bodyCls}`}>{description}</p>
      {(actionHref || onAction) && cta}
    </Card>
  );
}
